import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "./reduxHooks";
import { useIsFeatureEnabled } from "./useFeatureToggles";
import { actionCreators as MaintenanceActionCreator } from "modules/maintenance/actionCreator";
import { getIsMonthEndLockActive } from "modules/maintenance/selectors";
import { KnownFeatureToggles } from "modules/features/models";

/**
 * True when member-initiated edits should be locked because the Vital Objects month-end job is
 * running. Read-only - something on the page must call useMonthEndStatus to keep this fresh.
 */
export const useIsMonthEndLockActive = (): boolean => useAppSelector(getIsMonthEndLockActive);

/**
 * Fetches the month-end status on mount, and again whenever the tab regains focus.
 *
 * The focus refetch is what lets the lock appear for a member who left a tab open across the start
 * of month end - without it, a form opened beforehand would stay submittable.
 *
 * Does nothing at all while the feature toggle is off, which is its normal state. Without that
 * guard every profile and spending-account page view would make a pointless round trip through the
 * BFF and the API to Vital Objects for a status no one is going to act on. The submit-time guard in
 * modules/maintenance/guard.ts skips the call for the same reason.
 */
export const useMonthEndStatus = (): void => {
    const dispatch = useAppDispatch();
    const isLockoutFeatureEnabled = useIsFeatureEnabled(KnownFeatureToggles.EnableMonthEndMaintenanceLockout);

    useEffect(() => {
        if (!isLockoutFeatureEnabled) {
            return undefined;
        }

        const refresh = () => dispatch(MaintenanceActionCreator.getMonthEndStatus());

        refresh();

        window.addEventListener("focus", refresh);
        return () => window.removeEventListener("focus", refresh);
    }, [dispatch, isLockoutFeatureEnabled]);
};
